import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { Chart } from "./Chart.tsx";
import { useDexTickLiquidity } from "../hooks/useDexTickLiquidity.ts";
import { toTickBars, type TickBar } from "../lib/dexTicks.ts";

// Native CLMM tick-liquidity distribution, valued in USD. Each bar is an
// INTERVAL spanning [bps_lower, bps_upper) — the true CLMM range from an
// initialized tick to the next — so the active range (amber) visibly contains
// the current-price line, and wide tick gaps render as wide bars. Height is the
// USD value of the tokens the range holds now.

const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const VIOLET = "#9b6fe0";
const GRID = "rgba(255,255,255,0.08)";
const BORDER = "rgba(255,255,255,0.08)";

function fmtUsd(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  const s = a >= 1e9 ? (n / 1e9).toFixed(2) + "B"
    : a >= 1e6 ? (n / 1e6).toFixed(2) + "M"
    : a >= 1e3 ? (n / 1e3).toFixed(1) + "k"
    : n.toFixed(0);
  return "$" + s;
}

// Whole-dollar with thousands separators, for reference figures (e.g. the ETH
// rate) where the abbreviated form would round misleadingly ($1,756 not $1.8k).
function fmtUsdWhole(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return "$" + Math.round(n).toLocaleString("en-US");
}

// USD price per base token (e.g. USD per SAFE) — small values kept readable.
function fmtUsdPrice(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n >= 1) return "$" + n.toFixed(4);
  if (n >= 0.0001) return "$" + n.toFixed(6);
  return "$" + n.toExponential(3);
}

function fmtQty(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (a >= 1e3) return (n / 1e3).toFixed(1) + "k";
  if (a >= 1) return n.toFixed(2);
  return n.toPrecision(3);
}

function fmtBps(b: number): string {
  const pct = b / 100;
  const s = Math.abs(pct) >= 1 ? pct.toFixed(1) : pct.toFixed(2);
  return (b > 0 ? "+" : "") + s + "%";
}

function ageStr(secs: number | null): string {
  if (secs == null || !Number.isFinite(secs)) return "—";
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

const CLIP = 9000; // clamp bar edges to +/-90% so nothing blows the axis

export function DexTickLiquidityChart({
  venue,
  pair,
  clientId: _clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useDexTickLiquidity(venue, pair);
  const prof = q.data;
  const ok = prof?.status === "ok";

  const view = useMemo(
    () =>
      prof
        ? toTickBars(prof, CLIP)
        : {
            bars: [] as TickBar[], omitted: 0, clipBps: CLIP, maxValue: 0,
            usdAbove: 0, usdBelow: 0, ethUsd: null, ethUsdAgeSeconds: null,
            usdPerBase: null, basePerQuote: null, quoteUsdRate: null, quoteAsset: "quote",
            baseAsset: "base",
          },
    [prof],
  );

  const option = useMemo<EChartsOption>(() => {
    const ethUsd = view.ethUsd;
    // USD-per-base at a base/quote (e.g. WETH/SAFE) price p is p * eth_usd.
    const toUsdPrice = (p: number | null) =>
      p != null && ethUsd != null ? p * ethUsd : null;
    // base-per-quote (e.g. SAFE per WETH) is 1/p.
    const toBasePerQuote = (p: number | null) =>
      p != null && p !== 0 ? 1 / p : null;

    // x-axis bounds from the (rail-free) bars, padded, always including 0.
    let xMin = 0;
    let xMax = 0;
    for (const b of view.bars) {
      xMin = Math.min(xMin, Math.max(-CLIP, b.bpsLower));
      xMax = Math.max(xMax, Math.min(CLIP, b.bpsUpper));
    }
    const pad = Math.max(20, (xMax - xMin) * 0.04);

    return {
      backgroundColor: "transparent",
      textStyle: { color: MUTED },
      grid: { left: 66, right: 16, top: 16, bottom: 40 },
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(20,24,33,0.95)",
        borderColor: GRID,
        textStyle: { color: TEXT, fontSize: 12 },
        formatter: (p: unknown) => {
          const bar = (p as { data?: { bar?: TickBar } }).data?.bar;
          if (!bar) return "";
          const comp = [
            bar.amountBase && bar.amountBase > 0 ? `${fmtQty(bar.amountBase)} ${view.baseAsset}` : null,
            bar.amountQuote && bar.amountQuote > 0 ? `${fmtQty(bar.amountQuote)} ${view.quoteAsset}` : null,
          ].filter(Boolean).join(" + ") || "—";
          // Order both price lines LEFT edge -> RIGHT edge of the bar,
          // matching the axis and the bps line: lower raw price = more
          // negative bps = left edge. USD/base then reads ascending; the
          // native base-per-quote line reads descending (inverse frame) but
          // stays positionally aligned with the USD line above it.
          const pLeft =
            bar.priceLower != null && bar.priceUpper != null
              ? Math.min(bar.priceLower, bar.priceUpper)
              : bar.priceLower ?? bar.priceUpper;
          const pRight =
            bar.priceLower != null && bar.priceUpper != null
              ? Math.max(bar.priceLower, bar.priceUpper)
              : bar.priceUpper ?? bar.priceLower;
          const usdLo = fmtUsdPrice(toUsdPrice(pLeft));
          const usdHi = fmtUsdPrice(toUsdPrice(pRight));
          const spLeft = toBasePerQuote(pLeft);
          const spRight = toBasePerQuote(pRight);
          const secondary =
            spLeft != null && spRight != null
              ? `${fmtQty(spLeft)} → ${fmtQty(spRight)} ${view.baseAsset}/${view.quoteAsset}`
              : "";
          return [
            `<b>${fmtUsd(bar.valueUsd)}</b> · ${fmtBps(bar.bpsLower)} → ${fmtBps(bar.bpsUpper)}`,
            `range ${usdLo} → ${usdHi} per ${view.baseAsset}`,
            secondary,
            `${comp}`,
            bar.isActive
              ? "<i>active range (price is here)</i>"
              : bar.rawBpsLower >= 0
                ? "<i>sell-side inventory · fills as price rises</i>"
                : "<i>buy-side inventory · fills as price falls</i>",
          ].filter(Boolean).join("<br/>");
        },
      },
      xAxis: {
        type: "value",
        min: xMin - pad,
        max: xMax + pad,
        name: "distance from pool price",
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: MUTED, fontSize: 11 },
        axisLine: { lineStyle: { color: GRID } },
        axisLabel: { color: MUTED, fontSize: 11, formatter: (v: number) => fmtBps(v) },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        name: "value parked (USD)",
        nameTextStyle: { color: MUTED, fontSize: 11 },
        axisLine: { show: false },
        axisLabel: { color: MUTED, fontSize: 11, formatter: (v: number) => fmtUsd(v) },
        splitLine: { lineStyle: { color: GRID } },
      },
      series: [
        {
          type: "custom",
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          renderItem: (_params: any, api: any) => {
            const bpsL = Math.max(-CLIP, Math.min(CLIP, api.value(0)));
            const bpsU = Math.max(-CLIP, Math.min(CLIP, api.value(1)));
            const val = api.value(2);
            const pLow = api.coord([bpsL, 0]);
            const pHigh = api.coord([bpsU, val]);
            const x = Math.min(pLow[0], pHigh[0]);
            let w = Math.abs(pHigh[0] - pLow[0]);
            if (w < 2) w = 2; // keep dense/narrow intervals visible
            return {
              type: "rect",
              shape: { x, y: pHigh[1], width: w, height: pLow[1] - pHigh[1] },
              style: api.style(),
            };
          },
          encode: { x: [0, 1], y: 2 },
          data: view.bars.map((b) => ({
            value: [b.bpsLower, b.bpsUpper, b.value],
            bar: b,
            itemStyle: {
              color: b.isActive ? AMBER : VIOLET,
              opacity: b.isActive ? 1 : 0.78,
            },
          })),
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: { color: "rgba(255,255,255,0.4)", width: 1, type: "dashed" as const },
            label: { show: true, formatter: "price", color: MUTED, fontSize: 10, position: "insideEndTop" },
            data: [{ xAxis: 0 }],
          },
        },
      ],
    };
  }, [view]);

  const priceLine =
    ok && prof
      ? `${fmtUsdPrice(view.usdPerBase)} per ${view.baseAsset}` +
        (view.basePerQuote != null
          ? ` · ${fmtQty(view.basePerQuote)} ${view.baseAsset}/${view.quoteAsset}`
          : "")
      : "";

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <div>
          <div style={{ fontWeight: 500, color: TEXT }}>Liquidity by price (native pool ticks)</div>
          {ok && (view.usdAbove > 0 || view.usdBelow > 0) && (
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
              <span style={{ color: TEXT }}>{fmtUsd(view.usdAbove)}</span> above price ({view.baseAsset})
              {"  ·  "}
              <span style={{ color: TEXT }}>{fmtUsd(view.usdBelow)}</span> below ({view.quoteAsset})
            </div>
          )}
        </div>
        <span
          style={{
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 5,
            color: ok ? VIOLET : AMBER,
            border: `1px solid ${(ok ? VIOLET : AMBER)}55`,
            whiteSpace: "nowrap",
          }}
          title="Captured directly from the pool's initialized ticks (native CLMM liquidity), valued in USD via reference_prices."
        >
          {ok ? "native · USD" : (prof?.status ?? "…")}
        </span>
      </div>

      {q.isError ? (
        <div style={{ fontSize: 12, color: AMBER, padding: "24px 2px" }}>
          could not load tick liquidity
        </div>
      ) : ok && view.bars.length === 0 ? (
        <div style={{ fontSize: 13, color: MUTED, padding: "24px 2px", lineHeight: 1.5 }}>
          No initialized ticks near the current price in this snapshot.
        </div>
      ) : !prof ? (
        <div style={{ fontSize: 13, color: MUTED, padding: "24px 2px" }}>loading…</div>
      ) : prof.status !== "ok" ? (
        <div style={{ fontSize: 13, color: MUTED, padding: "24px 2px", lineHeight: 1.5 }}>
          {prof.status === "no_data" ? "No native tick snapshot available yet." : prof.status}
        </div>
      ) : (
        <Chart option={option} height={340} />
      )}

      {prof && ok && (
        <div
          style={{
            fontSize: 11,
            color: MUTED,
            marginTop: 6,
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 7,
            lineHeight: 1.5,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          pool price {priceLine} · tick {prof.current_tick} · {prof.tick_count} initialized ticks ·{" "}
          <span style={{ color: AMBER }}>amber = active range</span>
          {view.omitted > 0 && (
            <>
              {" "}· {view.omitted} full-range position{view.omitted === 1 ? "" : "s"} beyond ±
              {(view.clipBps / 100).toFixed(0)}% omitted
            </>
          )}
          <br />
          USD via reference_prices{view.quoteUsdRate != null ? ` · ${view.quoteAsset ?? ""} ${fmtUsdWhole(view.quoteUsdRate)}` : ""} ·{" "}
          rate {ageStr(view.ethUsdAgeSeconds)}
          {prof.anchor_ok === true && <span style={{ color: "#6fcf97" }}> · reconstruction verified</span>}
          {prof.anchor_ok === false && <span style={{ color: AMBER }}> · reconstruction mismatch</span>}
        </div>
      )}
    </div>
  );
}
